import { NodeSelection, TextSelection } from 'prosemirror-state'

import type { Command } from 'common/Prose/Commands'
import { setTrackedChangeCommand } from 'common/Prose/Plugins/TrackChanges/commands'
import { CommandName } from 'common/Prose/Plugins/TrackChanges/commands/types'

/**
 * Break out of a list if we are on the first position of the first direct child of an indented list item or on the first position of a list item with a gap cursor
 */
export const breakOutOfList: Command = ({ schema, selection, tr }, dispatch) => {
  const { $from, empty } = selection
  if (!empty) return false

  const parentNode = $from.node($from.depth - 1)
  if (parentNode?.type === schema.nodes.toggle_list) return false
  const isListItem = [schema.nodes.list_item, schema.nodes.vote_list_item].includes(parentNode?.type)
  // NOTE: only if the cursor is on the first position of the first child of a list item
  if (!isListItem && !parentNode?.type.groups.includes('list')) return false
  if (selection.from !== selection.$from.start()) return false

  const listItem = isListItem ? parentNode : $from.node()
  // There are tow cases: We are inside a list item (node is something) or we are on a gap cursors position inside the list item (node is null)
  const node = isListItem ? $from.node() : null

  if (node && node !== listItem.firstChild) return false
  if (!node && selection.from !== selection.$from.start()) return false // we are not on the gap cursor position on the start of the list item
  if (listItem.attrs.depth !== 0) return false // NOTE: if we are on an indented list item we just want to deindent

  const list = parentNode.type.groups.includes('list') ? parentNode : selection.$from.node(selection.$from.depth - 2)

  // NOTE: step 1: split of the list before and after the list item
  const splitBeforePos = node ? tr.selection.$from.before($from.depth - 1) : tr.selection.$from.before()
  if (listItem !== list.firstChild) tr.split(splitBeforePos)
  const splitAfterPos = node ? tr.selection.$from.after($from.depth - 1) : tr.selection.$from.after()
  if (listItem !== list.lastChild) tr.split(splitAfterPos)

  // NOTE: step 2: insert the new content
  const listBeforePos = node
    ? tr.selection.$from.before(tr.selection.$from.depth - 2)
    : tr.selection.$from.before(tr.selection.$from.depth - 1)
  const listAfterPos = node
    ? tr.selection.$from.after(tr.selection.$from.depth - 2)
    : tr.selection.$from.after(tr.selection.$from.depth - 1)

  tr.replaceWith(listBeforePos, listAfterPos, listItem.content)

  tr.setSelection(
    listItem.content.firstChild?.type.groups.includes('textWrap')
      ? TextSelection.create(tr.doc, listBeforePos + 1)
      : NodeSelection.create(tr.doc, listBeforePos),
  )

  setTrackedChangeCommand(tr, {
    name: CommandName.BREAK_OUT_OF_LIST,
    meta: {
      from: splitBeforePos,
      to: splitAfterPos,
    },
  })

  dispatch?.(tr)

  return true
}
